// Pure model + helpers for the broadcast "pipeline board" (kanban) view.
//
// A broadcast recipient is placed in exactly ONE column: the furthest stage it
// reached. The derivation is MONOTONIC over the recipient's milestone
// timestamps (a later milestone implies the earlier ones even when an
// intermediate webhook never arrived) — mirroring the onboarding funnel's
// furthest-stage model, so it is stable under out-of-order / missing webhooks.

import type { WhatsAppBroadcastRecipient } from "../types";

export type BroadcastStageKey =
  | "skipped"
  | "failed"
  | "sent"
  | "delivered"
  | "read"
  | "replied"
  | "create"
  | "buy"
  | "echo_started"
  | "final_delivered";

export type BroadcastBoardColumn = {
  key: BroadcastStageKey;
  label: string;
  /** Recipients whose furthest stage is exactly this column. */
  recipients: WhatsAppBroadcastRecipient[];
  total: number;
};

export type BroadcastStageResult = {
  key: BroadcastStageKey;
  /** Timestamp the recipient entered this furthest stage (for "in stage Nd"). */
  enteredAt: string | null;
};

// Display order, left → right. Skipped/Failed are off-the-happy-path lanes.
export const BROADCAST_STAGES: { key: BroadcastStageKey; label: string }[] = [
  { key: "skipped", label: "Skipped" },
  { key: "failed", label: "Failed" },
  { key: "sent", label: "Sent" },
  { key: "delivered", label: "Delivered" },
  { key: "read", label: "Read" },
  { key: "replied", label: "Replied" },
  { key: "create", label: "/create" },
  { key: "buy", label: "/buy" },
  { key: "echo_started", label: "Echo started" },
  { key: "final_delivered", label: "Final delivered" },
];

const STAGE_LABELS: Record<BroadcastStageKey, string> = Object.fromEntries(
  BROADCAST_STAGES.map((s) => [s.key, s.label]),
) as Record<BroadcastStageKey, string>;

export function broadcastStageLabel(key: BroadcastStageKey): string {
  return STAGE_LABELS[key] ?? key;
}

// Happy-path ranks (skipped/failed are handled separately, before this).
const SENT = 2;
const DELIVERED = 3;
const READ = 4;
const REPLIED = 5;
const CREATE = 6;
const BUY = 7;
const ECHO = 8;
const FINAL = 9;

const RANK_TO_STAGE: Record<number, BroadcastStageKey> = {
  [SENT]: "sent",
  [DELIVERED]: "delivered",
  [READ]: "read",
  [REPLIED]: "replied",
  [CREATE]: "create",
  [BUY]: "buy",
  [ECHO]: "echo_started",
  [FINAL]: "final_delivered",
};

const CREATE_REPLY_TEXTS = new Set([
  "create",
  "create movie",
  "create my movie",
  "create new echo",
  "start my movie",
  "start new echo",
]);

/** A reply whose text is the "Create my movie" CTA / `/create` command. */
export function isCreateReply(recipient: WhatsAppBroadcastRecipient): boolean {
  if (recipient.first_create_command_at) return true;
  const text = recipient.reply_text?.trim().toLowerCase().replace(/\s+/g, " ");
  if (!text) return false;
  return text.split(" ")[0] === "/create" || CREATE_REPLY_TEXTS.has(text);
}

function isSkipped(recipient: WhatsAppBroadcastRecipient): boolean {
  return (
    recipient.status === "skipped" ||
    recipient.provider_status === "skipped" ||
    (recipient.skip_reasons?.length ?? 0) > 0
  );
}

function isFailed(recipient: WhatsAppBroadcastRecipient): boolean {
  return Boolean(
    recipient.first_failed_at ||
      recipient.provider_status === "failed" ||
      recipient.status === "failed" ||
      recipient.provider_error_code,
  );
}

// The furthest happy-path rank reached (0 when nothing past acceptance), plus
// the timestamp of that milestone. Each branch takes the max so a present late
// milestone implies all earlier ones.
function happyProgress(recipient: WhatsAppBroadcastRecipient): {
  rank: number;
  at: string | null;
} {
  let rank = 0;
  let at: string | null = null;
  const consider = (present: unknown, value: number, ts?: string | null) => {
    if (present && value > rank) {
      rank = value;
      at = ts ?? at;
    }
  };

  consider(
    recipient.first_sent_at ||
      recipient.sent_at ||
      recipient.provider_status === "sent",
    SENT,
    recipient.first_sent_at || recipient.sent_at,
  );
  consider(
    recipient.first_delivered_at || recipient.provider_status === "delivered",
    DELIVERED,
    recipient.first_delivered_at,
  );
  consider(
    recipient.first_read_at || recipient.provider_status === "read",
    READ,
    recipient.first_read_at,
  );
  consider(
    recipient.first_inbound_after_broadcast_at || recipient.reply_text,
    REPLIED,
    recipient.first_inbound_after_broadcast_at,
  );
  consider(isCreateReply(recipient), CREATE, recipient.first_create_command_at);
  consider(recipient.first_buy_command_at, BUY, recipient.first_buy_command_at);
  consider(
    recipient.first_echo_started_at ||
      recipient.first_generation_requested_at ||
      recipient.first_final_video_created_at,
    ECHO,
    recipient.first_echo_started_at ||
      recipient.first_generation_requested_at ||
      recipient.first_final_video_created_at,
  );
  consider(
    recipient.first_final_video_delivered_at,
    FINAL,
    recipient.first_final_video_delivered_at,
  );

  return { rank, at };
}

/** Furthest stage reached + when it was entered. Monotonic, deterministic. */
export function deriveRecipientStage(
  recipient: WhatsAppBroadcastRecipient,
): BroadcastStageResult {
  if (isSkipped(recipient)) {
    return { key: "skipped", enteredAt: recipient.status_updated_at ?? null };
  }
  const { rank, at } = happyProgress(recipient);
  // A failure that never progressed past delivery shows as Failed; a recipient
  // that got further (rare late-failure) keeps its furthest happy stage.
  if (isFailed(recipient) && rank < DELIVERED) {
    return { key: "failed", enteredAt: recipient.first_failed_at ?? null };
  }
  if (rank >= SENT) {
    return { key: RANK_TO_STAGE[rank], enteredAt: at };
  }
  // Accepted / queued / sending but no sent milestone yet → "awaiting delivery".
  return {
    key: "sent",
    enteredAt: recipient.accepted_at ?? recipient.sent_at ?? null,
  };
}

/** Group recipients into the full ordered set of columns (incl. empty ones). */
export function bucketRecipients(
  recipients: WhatsAppBroadcastRecipient[],
): BroadcastBoardColumn[] {
  const byStage = new Map<BroadcastStageKey, WhatsAppBroadcastRecipient[]>();
  for (const stage of BROADCAST_STAGES) byStage.set(stage.key, []);
  for (const recipient of recipients) {
    const { key } = deriveRecipientStage(recipient);
    byStage.get(key)!.push(recipient);
  }
  return BROADCAST_STAGES.map((stage) => ({
    key: stage.key,
    label: stage.label,
    recipients: byStage.get(stage.key)!,
    total: byStage.get(stage.key)!.length,
  }));
}

/** Human label for a recipient — contact name, else the WhatsApp number. */
export function recipientDisplayName(
  recipient: WhatsAppBroadcastRecipient,
): string {
  const name = recipient.contact_name?.trim();
  if (
    name &&
    name.toLowerCase() !== "whatsapp" &&
    name !== recipient.whatsapp_user_id
  ) {
    return name;
  }
  return (
    recipient.whatsapp_user_id || String(recipient.contact_id ?? recipient.id)
  );
}

/** Emoji flag from an ISO-3166-1 alpha-2 code, or "" when unknown. */
export function recipientFlag(iso2?: string | null): string {
  const code = (iso2 ?? "").trim().toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (code.charCodeAt(0) - 65),
    A + (code.charCodeAt(1) - 65),
  );
}

/** Whole days since the recipient entered its stage; null when unknown. */
export function daysInStage(
  enteredAt?: string | null,
  now: Date = new Date(),
): number | null {
  if (!enteredAt) return null;
  const entered = new Date(enteredAt).getTime();
  if (!Number.isFinite(entered)) return null;
  const diffMs = now.getTime() - entered;
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / 86_400_000);
}

// Conservative away-message / auto-responder patterns (EN/FR/AR), so the
// "Replied" lane is not inflated by recipients' own business bots. Flagged in
// the UI (never silently dropped).
const AUTO_RESPONDER_PATTERNS = [
  "nous ne sommes pas disponibles",
  "merci pour votre message",
  "we are not available",
  "currently unavailable",
  "out of office",
  "away from my phone",
  "automatic reply",
  "auto-reply",
  "this is an automated",
  "لسنا متوفرين",
  "شكرا لك على تواصلك",
  "شكراً لك على رسالتك",
];

export function isLikelyAutoResponder(text?: string | null): boolean {
  if (!text) return false;
  const t = text.toLowerCase();
  return AUTO_RESPONDER_PATTERNS.some((p) => t.includes(p));
}
